import React from 'react';

import { Flex } from '@chakra-ui/react';
import { useDispatch } from 'react-redux';
import {
  setTimelinesSameSettingAsFeedbackFormId,
  useReviewCreationData,
  useTimelines,
  useTimelinesSameSetting,
} from 'reducers/reviewCreationSlice';

import { ListWarning } from 'uiToolkitV1/ListWarning';

import { useGetTimelines, useSaveTimelines } from '../../Hooks/dataHooks/timelinesDataHooks';
import { ReviewCreationFooter } from '../../ReviewCreationFooter';
import { MultiViewStepAccordionWrapper } from '../../components/MultiViewStepAccordionWrapper';
import { SkeletonLoaderMultiViewVariant } from '../../components/SkeletonLoaderReviewCreation';
import { TimelinesSelection } from './TimelinesSelection';

export const validateTimelinesDates = (timelines) => {
  const isInvalidDatePresent = timelines.some(({ reviewTimelineSettingList }) =>
    reviewTimelineSettingList.some(({ startDate, endDate }) => !startDate || !endDate)
  );
  return !isInvalidDatePresent;
};

const Footer = () => {
  const timelines = useTimelines();

  const isValidTimelines = validateTimelinesDates(timelines);
  const saveDataMutation = useSaveTimelines();

  const isContinueDisabled = !isValidTimelines || saveDataMutation.isLoading;

  const handleContinue = () => {
    saveDataMutation.mutate();
  };

  return <ReviewCreationFooter isContinueDisabled={isContinueDisabled} onContinue={handleContinue} />;
};

const InfoHeader = () => (
  <ListWarning warningText="This section is editable even after the review cycle has been launched" />
);

const SingleViewSettings = ({ view, feedbackFormId, feedbackFormName, index: viewIndex, singleViewOnly }) => {
  const settingSameAsFormId = useTimelinesSameSetting({ feedbackFormId });

  const dispatch = useDispatch();

  const timelines = useTimelines();
  const firstViewName = timelines?.[0]?.feedbackFormName;
  const firstFeedbackFormId = timelines?.[0]?.feedbackFormId;

  const { isAllowSameSettingAsFirstView } = timelines[viewIndex];

  const onChangeHandler = () => {
    dispatch(
      setTimelinesSameSettingAsFeedbackFormId({
        feedbackFormId,
        settingSameAsFormId: settingSameAsFormId ? null : firstFeedbackFormId,
      })
    );
  };

  if (singleViewOnly) {
    return (
      <Flex flexDirection="column" gap={4}>
        <InfoHeader />
        <TimelinesSelection feedbackFormId={feedbackFormId} />
      </Flex>
    );
  }

  return (
    <MultiViewStepAccordionWrapper
      firstViewName={firstViewName}
      onChangeHandler={onChangeHandler}
      settingSameAsFormId={settingSameAsFormId}
      view={view}
      index={viewIndex}
      isAllowSameSettingAsFirstView={isAllowSameSettingAsFirstView}
      feedbackFormName={feedbackFormName}
    >
      <Flex flexDirection="column" gap={4}>
        <InfoHeader />

        <TimelinesSelection feedbackFormId={feedbackFormId} />
      </Flex>
    </MultiViewStepAccordionWrapper>
  );
};

const Timelines = () => {
  const { reviewCycleId } = useReviewCreationData();
  const timelines = useTimelines();

  const { isSuccess, isLoading } = useGetTimelines({ reviewCycleId });

  if (isLoading) {
    return <SkeletonLoaderMultiViewVariant />;
  }

  if (!isSuccess) return null;

  return (
    <>
      {timelines.map(({ cohort, feedbackFormId, feedbackFormName }, index) => (
        <SingleViewSettings
          view={cohort}
          feedbackFormId={feedbackFormId}
          index={index}
          singleViewOnly={timelines.length === 1}
          feedbackFormName={feedbackFormName}
        />
      ))}
      <Footer />
    </>
  );
};

export { Timelines };
